import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getABTests, getTeams, getBrands, deleteABTest } from '@/lib/api';
import { Input } from '@/components/ui/input';
import {
  MagnifyingGlass, Plus, Trash, Eye, Flask, Trophy, ArrowUp, ArrowDown, Minus, Clock
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  'Not Started': '#E8E8E8', 'Running': '#B3D4FF', 'Completed': '#34C759', 'Implemented': '#10B981',
};
const WINNER_ICONS = {
  'A': { icon: 'A', color: '#FF3B30' },
  'B': { icon: 'B', color: '#32B4AC' },
  'Inconclusive': { icon: '?', color: '#FFB800' },
};

export default function ABTestingPage() {
  const [tests, setTests] = useState([]);
  const [teams, setTeams] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBrand, setFilterBrand] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWinner, setFilterWinner] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterBrand) params.brand_id = filterBrand;
      if (filterStatus) params.status = filterStatus;
      if (filterWinner) params.winner = filterWinner;
      if (filterTeam) params.assigned_team = filterTeam;
      const [testsRes, teamsRes, brandsRes] = await Promise.all([
        getABTests(params), getTeams(), getBrands()
      ]);
      setTests(testsRes.data);
      setTeams(teamsRes.data);
      setBrands(brandsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterBrand, filterStatus, filterWinner, filterTeam]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this A/B test?')) return;
    await deleteABTest(id);
    setTests(prev => prev.filter(t => t.id !== id));
  };

  const teamMap = Object.fromEntries(teams.map(t => [t.id, t.name]));
  const brandMap = Object.fromEntries(brands.map(b => [b.id, b.name]));

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="ab-testing-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F] flex items-center gap-2">
            <Flask size={24} weight="fill" className="text-[#32B4AC]" />
            A/B Testing
          </h1>
          <p className="text-sm text-[#555555] mt-1">Track experiments, compare variants, and make data-driven decisions</p>
        </div>
        <button
          data-testid="create-ab-test-btn"
          onClick={() => navigate('/ab-tests/new')}
          className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors flex items-center gap-2"
        >
          <Plus size={14} weight="bold" />
          New A/B Test
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="ab-filter-brand" value={filterBrand} onChange={e => setFilterBrand(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white text-[#0F0F0F]">
          <option value="">All Brands</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select data-testid="ab-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Statuses</option>
          <option value="Not Started">Not Started</option>
          <option value="Running">Running</option>
          <option value="Completed">Completed</option>
          <option value="Implemented">Implemented</option>
        </select>
        <select data-testid="ab-filter-winner" value={filterWinner} onChange={e => setFilterWinner(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Winners</option>
          <option value="A">Variant A</option>
          <option value="B">Variant B</option>
          <option value="Inconclusive">Inconclusive</option>
        </select>
        <select data-testid="ab-filter-team" value={filterTeam} onChange={e => setFilterTeam(e.target.value)} className="h-8 text-xs border border-[#E8E8E8] rounded-md px-2 bg-white">
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-[#555555] text-sm py-8">Loading tests...</div>
      ) : (
        <div className="border border-[#E8E8E8] rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ab-tests-table">
              <thead>
                <tr className="bg-[#F4F4F4]">
                  <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Test Name</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Linked Task</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Brand</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Status</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Winner</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">% Change</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Duration</th>
                  <th className="text-left px-3 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-[#555555]">Updated</th>
                  <th className="px-3 py-2.5 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {tests.map(test => (
                  <tr
                    key={test.id}
                    onClick={() => navigate(`/ab-tests/${test.id}`)}
                    className="border-t border-[#E8E8E8] hover:bg-[#F4F4F4] cursor-pointer transition-colors"
                    data-testid={`ab-test-row-${test.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Flask size={14} weight="fill" className="text-[#32B4AC] shrink-0" />
                        <span className="font-medium text-[#0F0F0F] truncate max-w-[200px]">{test.test_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555] truncate max-w-[150px]">{test.linked_task_title || '-'}</td>
                    <td className="px-3 py-3 text-xs text-[#555555]">{brandMap[test.brand_id] || '-'}</td>
                    <td className="px-3 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: (STATUS_COLORS[test.status] || '#E8E8E8') + '60' }}>
                        {test.status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {test.winner ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{
                          backgroundColor: (WINNER_ICONS[test.winner]?.color || '#E8E8E8') + '18',
                          color: WINNER_ICONS[test.winner]?.color || '#555555'
                        }}>
                          {test.winner === 'Inconclusive' ? 'Inconclusive' : `Variant ${test.winner}`}
                        </span>
                      ) : <span className="text-xs text-[#E8E8E8]">-</span>}
                    </td>
                    <td className="px-3 py-3">
                      {test.percent_change != null ? (
                        <span className={`text-xs font-semibold flex items-center gap-0.5 ${test.percent_change > 0 ? 'text-[#34C759]' : test.percent_change < 0 ? 'text-[#FF3B30]' : 'text-[#555555]'}`}>
                          {test.percent_change > 0 ? <ArrowUp size={10} /> : test.percent_change < 0 ? <ArrowDown size={10} /> : <Minus size={10} />}
                          {test.percent_change > 0 ? '+' : ''}{test.percent_change}%
                        </span>
                      ) : <span className="text-xs text-[#E8E8E8]">-</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555]">
                      {test.duration_days != null ? `${test.duration_days}d` : '-'}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#555555]">
                      {test.updated_at ? new Date(test.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/ab-tests/${test.id}`); }} className="p-1 hover:bg-[#E8E8E8] rounded-md" data-testid={`view-ab-test-${test.id}`}>
                          <Eye size={14} className="text-[#555555]" />
                        </button>
                        <button onClick={(e) => handleDelete(test.id, e)} className="p-1 hover:bg-red-50 rounded-md" data-testid={`delete-ab-test-${test.id}`}>
                          <Trash size={14} className="text-[#555555] hover:text-[#FF3B30]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tests.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12">
                      <Flask size={32} weight="fill" className="text-[#E8E8E8] mx-auto mb-2" />
                      <p className="text-sm text-[#555555]">No A/B tests yet. Create your first experiment.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
